<script setup>
import { computed, ref, watch } from 'vue';
import { t } from '../i18n';
import BottomSheet from './BottomSheet.vue';

// Tag picker of the news filter (docs/news/SPEC.md, decisions 20, 21): nine categories, tickers split by
// kind, most used tags first; a search over all of them.
const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  // GET /news/tags → categories: [{ key, tags: [{ tag, count, subtype }] }]
  categories: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  selected: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close', 'toggle']);

const TICKER_KINDS = ['share_ru', 'share_foreign', 'crypto', 'index', 'currency', 'commodity_quote', null];
const COLLAPSED = 12;

const query = ref('');
const expanded = ref(new Set());

watch(() => props.open, (open) => {
  if (open) {
    query.value = '';
  }
});

// Sections to show: categories, and tickers by kind.
const groups = computed(() => {
  const needle = query.value.trim().toLowerCase().replace(/^#/, '');
  const result = [];
  for (const category of props.categories) {
    const tags = needle ? category.tags.filter((tag) => tag.tag.includes(needle)) : category.tags;
    if (category.key === 'ticker') {
      for (const kind of TICKER_KINDS) {
        const kindTags = tags.filter((tag) => (tag.subtype ?? null) === kind);
        if (kindTags.length > 0) {
          result.push({
            key: `ticker.${kind ?? 'other'}`,
            title: t('news.category.ticker'),
            subtitle: t(`news.tickerKind.${kind ?? 'other'}`),
            tags: kindTags,
          });
        }
      }
    } else if (tags.length > 0) {
      result.push({ key: category.key, title: t(`news.category.${category.key}`), subtitle: '', tags });
    }
  }
  return result;
});

function visibleTags(group) {
  return query.value || expanded.value.has(group.key) ? group.tags : group.tags.slice(0, COLLAPSED);
}

function expand(group) {
  expanded.value = new Set([...expanded.value, group.key]);
}

function isSelected(tag) {
  return props.selected.includes(tag);
}
</script>

<template>
  <BottomSheet
    :open="open"
    :title="t('news.filterTitle')"
    @close="emit('close')"
  >
    <div class="px-5 pb-2">
      <input
        v-model="query"
        type="search"
        class="h-11 w-full rounded-xl border-0 bg-slate-800 px-3.5 text-[0.9375rem] text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400 sm:h-9 sm:text-sm"
        :placeholder="t('news.searchTags')"
        :aria-label="t('news.searchTags')"
      >
    </div>

    <p
      v-if="loading"
      class="px-5 py-4 text-[0.9375rem] text-slate-400"
    >
      {{ t('common.loading') }}
    </p>
    <p
      v-else-if="groups.length === 0"
      class="px-5 py-4 text-[0.9375rem] text-slate-400"
    >
      {{ t('news.noTagsFound') }}
    </p>

    <section
      v-for="group in groups"
      :key="group.key"
      class="px-5 pt-3"
    >
      <h3 class="mb-2 text-[0.8125rem] text-slate-500">
        {{ group.title }}<template v-if="group.subtitle">
          · {{ group.subtitle }}
        </template>
      </h3>
      <div class="flex flex-wrap gap-1.5">
        <button
          v-for="tag in visibleTags(group)"
          :key="tag.tag"
          type="button"
          class="rounded-full px-2.5 py-1 text-[0.8125rem] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          :class="isSelected(tag.tag) ? 'bg-slate-100 font-medium text-slate-950' : 'bg-slate-800 text-slate-300'"
          :aria-pressed="isSelected(tag.tag)"
          @click="emit('toggle', tag.tag)"
        >
          #{{ group.key.startsWith('ticker') ? tag.tag.toUpperCase() : tag.tag }}
        </button>
        <button
          v-if="!query && !expanded.has(group.key) && group.tags.length > COLLAPSED"
          type="button"
          class="rounded-full px-2.5 py-1 text-[0.8125rem] font-medium text-slate-400 hover:text-slate-200"
          @click="expand(group)"
        >
          {{ t('news.showAllTags', { count: group.tags.length }) }}
        </button>
      </div>
    </section>
  </BottomSheet>
</template>
